import * as Sentry from "@sentry/nextjs"
import { keepPreviousData, useIsRestoring, useQuery, useQueryClient } from "@tanstack/react-query"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useMemo } from "react"
import { useHydrated } from "@/hooks"
import type { TAuth } from "@/types"
import { AuthContext, api, queryKeys, refreshAPIAuth, ym } from "@/utils"
import { useImpersonate } from "@/utils/impersonate"
import { AppLoader } from "./utils"

const apiHeaders = api.defaults.headers

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()

  // Wait for ready
  const hydrated = useHydrated()
  const isRestoring = useIsRestoring()
  const isReady = hydrated && !isRestoring

  // Create Auth methods
  const queryClient = useQueryClient()
  const { login, logout, refresh } = useAuthMethods(queryClient)

  // Refresh auth token periodically
  // The token is restored from the persisted cache, so it must be in the header
  // before the query decides to run — otherwise the session is never validated
  const cachedAuth = queryClient.getQueryData<TAuth>(queryKeys.auth)
  if (cachedAuth?.token) apiHeaders.Authorization = `Bearer ${cachedAuth.token}`
  const hasToken = isReady && !!apiHeaders.Authorization
  // Every cached session must be revalidated on start: drop staleTime, otherwise
  // enabling a fresh cached query never fetches (tanstack only fetches optionally when stale)
  const {
    data: auth,
    isFetching,
    isFetched,
    isError,
    error,
  } = useQuery({
    enabled: hasToken,
    queryFn: refreshAPIAuth,
    queryKey: queryKeys.auth,
    retry: false,
    throwOnError: false,
    gcTime: 60e3 * 60 * 24 * 24, // 24 days
    refetchInterval: 60e3 * 30, // 30 minutes
    placeholderData: keepPreviousData,
    refetchOnMount: hasToken ? "always" : false,
    refetchOnWindowFocus: hasToken ? "always" : false,
  })
  apiHeaders.Authorization = auth?.token ? `Bearer ${auth.token}` : null

  // Handle auth state changes
  useEffect(() => {
    if (!isReady) return
    if (!isFetched || isFetching) return
    if (isError) {
      const status = (error as any).response?.status
      if (status === 401 || status === 403) {
        logout()
        if (pathname !== "/login") {
          router.replace("/login")
        }
        return
      }
      return
    }

    if (!auth?.token && hasToken) {
      logout()
      return
    }

    if (auth?.token && auth?.user) login(auth)
  }, [isReady, isFetched, isFetching, isError, auth, hasToken, pathname])

  // Apply impersonation if active
  const { value: impersonate } = useImpersonate()
  const finalAuth = (auth && impersonate ? { ...auth, user: { ...auth.user, ...impersonate.user } } : auth) as TAuth

  // Wait for initial auth state to be ready
  if (!isReady) return <AppLoader />

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        refresh,
        auth: finalAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function useAuthMethods(queryClient: ReturnType<typeof useQueryClient>) {
  const { reset } = useImpersonate()

  return useMemo(
    () => ({
      login: (data: TAuth) => {
        queryClient.setQueryData(queryKeys.auth, data)

        if (data.token) apiHeaders.Authorization = `Bearer ${data.token}`

        if (data.user) {
          const { company, user } = data

          // Initialize Sentry with user data
          Sentry.setUser({ id: user.id, email: user.email })

          // Initialize Yandex Metrica with user data
          if (data.user.is_super) ym("disable")
          else
            ym("userParams", {
              UserID: user.id,
              role: user.role,
              company: company?.id,
            })
        }
      },
      logout: () => {
        reset() // drops impersonation, but restores its token — clear the header after it
        apiHeaders.Authorization = null
        // resetQueries, not removeQueries: placeholderData (keepPreviousData) keeps
        // serving the removed session, so the app stays logged in and loops on 401
        queryClient.resetQueries({ queryKey: queryKeys.auth })
        Sentry.setUser(null)
      },
      refresh: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.auth })
      },
    }),
    [queryClient],
  )
}
